import { useEffect, useState } from "react";
import { ShoppingBag, ShoppingCart, Plus, Minus, Trash2, X, CreditCard, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getShopProducts, createOrder } from "@/lib/api";
import { useOutletContext } from "react-router-dom";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import RecaptchaWrapper from "@/components/RecaptchaWrapper";

function ShopPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    // Initialize cart from localStorage
    const savedCart = localStorage.getItem("futsalGentCart");
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [checkoutForm, setCheckoutForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postalCode: "",
  });
  const [processing, setProcessing] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetailOpen, setProductDetailOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;
  const { verifyRecaptcha } = useRecaptcha();

  // Get all images for a product (main + extras)
  const getProductImages = (product) => {
    const images = [];
    if (product.image_url) images.push(product.image_url);
    if (product.image_urls && product.image_urls.length > 0) {
      images.push(...product.image_urls);
    }
    return images;
  };

  const openProductDetail = (product) => {
    setSelectedProduct(product);
    setCurrentImageIndex(0);
    setProductDetailOpen(true);
  };

  const nextImage = () => {
    if (!selectedProduct) return;
    const images = getProductImages(selectedProduct);
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    if (!selectedProduct) return;
    const images = getProductImages(selectedProduct);
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await getShopProducts();
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("futsalGentCart", JSON.stringify(cart));
  }, [cart]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('nl-BE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const addToCart = (product) => {
    if (!product.in_stock) {
      toast.error("Dit artikel is niet op voorraad");
      return;
    }
    
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === product.id);
      if (existingItem) {
        return prevCart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
    toast.success(`${product.name} aan winkelmand toegevoegd`);
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem("futsalGentCart");
  };

  const cartTotal = cart.reduce((total, item) => total + item.price * item.quantity, 0);
  const cartItemCount = cart.reduce((count, item) => count + item.quantity, 0);

  const categories = ["all", ...new Set(products.map((p) => p.category))];
  const filteredProducts = categoryFilter === "all" 
    ? products 
    : products.filter((p) => p.category === categoryFilter);

  const handleCheckoutChange = (e) => {
    setCheckoutForm({ ...checkoutForm, [e.target.name]: e.target.value });
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setProcessing(true);
    
    try {
      // Verify reCAPTCHA first
      const recaptchaToken = await verifyRecaptcha("shop_checkout");
      
      // Create order items from cart
      const orderItems = cart.map(item => ({
        product_id: item.id,
        product_name: item.name,
        quantity: item.quantity,
        price: item.price,
        image_url: item.image_url
      }));
      
      // Create order in backend
      await createOrder({
        customer_name: checkoutForm.name,
        customer_email: checkoutForm.email,
        customer_phone: checkoutForm.phone,
        shipping_address: checkoutForm.address,
        shipping_city: checkoutForm.city,
        shipping_postal: checkoutForm.postalCode,
        items: orderItems,
        subtotal: cartTotal,
        total: cartTotal,
        recaptcha_token: recaptchaToken
      });
      
      toast.success("Bestelling succesvol geplaatst! We nemen binnenkort contact met je op.");
      clearCart();
      setCheckoutOpen(false);
      setCartOpen(false);
      setCheckoutForm({
        name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        postalCode: "",
      });
    } catch (error) {
      console.error("Fout bij het plaatsen van de bestelling:", error);
      toast.error("Bestelling mislukt. Probeer het opnieuw.");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse">
          <div className={`h-10 w-48 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded mb-8`} />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={`h-72 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded-lg`} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="shop-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <ShoppingBag className={`h-10 w-10 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-500'}`} />
            <div>
              <h1 className={`text-3xl md:text-4xl font-black font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>SHOP</h1>
              <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>
                Officiële Futsal Gent merchandise
              </p>
            </div>
          </div>
          
          {/* Cart Button */}
          <Sheet open={cartOpen} onOpenChange={setCartOpen}>
            <SheetTrigger asChild>
              <Button 
                variant="outline" 
                className={`relative ${isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-300 text-slate-900 hover:bg-slate-100'}`}
                data-testid="cart-button"
              >
                <ShoppingCart className="h-5 w-5" />
                {cartItemCount > 0 && (
                  <span className="absolute -top-2 -right-2 h-5 w-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent className={`w-full sm:max-w-md ${isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'}`}>
              <SheetHeader>
                <SheetTitle className={isDark ? 'text-white' : 'text-slate-900'}>
                  Winkelwagen ({cartItemCount} stuk(ken))
                </SheetTitle>
              </SheetHeader>
              
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64">
                  <ShoppingCart className={`h-16 w-16 mb-4 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
                  <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Je winkelwagen is leeg</p>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto py-4 space-y-4">
                    {cart.map((item) => (
                      <div 
                        key={item.id} 
                        className={`flex gap-4 p-4 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}
                        data-testid={`cart-item-${item.id}`}
                      >
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.name} className="w-20 h-20 object-cover rounded" />
                        ) : (
                          <div className={`w-20 h-20 flex items-center justify-center rounded ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                            <ShoppingBag className={`h-8 w-8 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                          </div>
                        )}
                        <div className="flex-1">
                          <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.name}</h4>
                          <p className={`text-sm ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-blue-600'}`}>
                            {formatPrice(item.price)}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <Button 
                              size="icon" 
                              variant="outline" 
                              className={`h-7 w-7 ${isDark ? 'border-white/20' : 'border-slate-300'}`}
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              data-testid={`decrease-${item.id}`}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className={`w-8 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {item.quantity}
                            </span>
                            <Button 
                              size="icon" 
                              variant="outline" 
                              className={`h-7 w-7 ${isDark ? 'border-white/20' : 'border-slate-300'}`}
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              data-testid={`increase-${item.id}`}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                              onClick={() => removeFromCart(item.id)}
                              data-testid={`remove-${item.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {formatPrice(item.price * item.quantity)}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  <SheetFooter className={`border-t pt-4 ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                    <div className="w-full space-y-4">
                      <div className="flex justify-between items-center">
                        <span className={`text-lg ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Totaal:</span>
                        <span className={`text-2xl font-bold ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-blue-600'}`}>
                          {formatPrice(cartTotal)}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          className={`flex-1 ${isDark ? 'border-white/20 text-white' : 'border-slate-300 text-slate-700'}`}
                          onClick={clearCart}
                        >
                          Winkelwagen legen
                        </Button>
                        <Button 
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                          onClick={() => setCheckoutOpen(true)}
                          data-testid="checkout-button"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Afrekenen
                        </Button>
                      </div>
                    </div>
                  </SheetFooter>
                </>
              )}
            </SheetContent>
          </Sheet>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger 
              className={`w-48 ${isDark ? 'bg-slate-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
              data-testid="category-filter"
            >
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className={isDark ? 'bg-slate-800 border-white/10' : 'bg-white border-slate-200'}>
              {categories.map((cat) => (
                <SelectItem 
                  key={cat} 
                  value={cat}
                  className={isDark ? 'text-white hover:bg-slate-700' : 'text-slate-900 hover:bg-slate-100'}
                >
                  {cat === "all" ? "All Categories" : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => {
              const imageCount = getProductImages(product).length;
              return (
                <Card 
                  key={product.id}
                  className={`card-hover overflow-hidden cursor-pointer ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}
                  data-testid={`product-${product.id}`}
                  onClick={() => openProductDetail(product)}
                >
                  <div className="relative">
                    {product.image_url ? (
                      <div className="aspect-square overflow-hidden">
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ) : (
                      <div className={`aspect-square flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                        <ShoppingBag className={`h-16 w-16 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
                      </div>
                    )}
                    {imageCount > 1 && (
                      <Badge className="absolute top-2 right-2 bg-blue-600 text-white text-xs">
                        {imageCount} foto's
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{product.name}</h3>
                      {!product.in_stock && (
                        <Badge variant="outline" className="text-red-500 border-red-500/50 text-xs">
                          Uitverkocht
                        </Badge>
                      )}
                    </div>
                    <p className={`text-sm mb-4 line-clamp-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className={`text-xl font-bold ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-blue-600'}`}>
                        {formatPrice(product.price)}
                      </span>
                      <Button 
                        size="sm"
                        className={product.in_stock ? "bg-blue-600 hover:bg-blue-700" : ""}
                        disabled={!product.in_stock}
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart(product);
                        }}
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Toevoegen
                      </Button>
                    </div>
                    <Badge variant="outline" className={`mt-3 ${isDark ? 'border-white/20 text-slate-400' : 'border-slate-300 text-slate-600'}`}>
                      {product.category}
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <ShoppingBag className={`h-20 w-20 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
            <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Binnenkort beschikbaar!
            </h2>
            <p className={`max-w-md mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Onze webshop wordt momenteel voorbereid. Kom binnenkort terug voor officiële Futsal Gent merchandise!
            </p>
          </div>
        )}

        {/* Checkout Modal */}
        <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
          <DialogContent className={`max-w-lg ${isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'}`}>
            <DialogHeader>
              <DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>Afrekenen</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleCheckout} className="space-y-4">
              <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Totaal:</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-blue-600'}`}>
                  {formatPrice(cartTotal)}
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                  {cartItemCount} stuk(ken)
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Volledige naam *</Label>
                  <Input
                    name="name"
                    value={checkoutForm.name}
                    onChange={handleCheckoutChange}
                    required
                    className={isDark ? 'bg-slate-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}
                    placeholder="John Doe"
                    data-testid="checkout-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>E-mail *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={checkoutForm.email}
                    onChange={handleCheckoutChange}
                    required
                    className={isDark ? 'bg-slate-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}
                    placeholder="john@example.com"
                    data-testid="checkout-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Telefoon *</Label>
                <Input
                  name="phone"
                  type="tel"
                  value={checkoutForm.phone}
                  onChange={handleCheckoutChange}
                  required
                  className={isDark ? 'bg-slate-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}
                  placeholder="+32 xxx xx xx xx"
                  data-testid="checkout-phone"
                />
              </div>

              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Adres *</Label>
                <Input
                  name="address"
                  value={checkoutForm.address}
                  onChange={handleCheckoutChange}
                  required
                  className={isDark ? 'bg-slate-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}
                  placeholder="Straat en nummer"
                  data-testid="checkout-address"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Stad *</Label>
                  <Input
                    name="city"
                    value={checkoutForm.city}
                    onChange={handleCheckoutChange}
                    required
                    className={isDark ? 'bg-slate-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}
                    placeholder="Gent"
                    data-testid="checkout-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Postcode *</Label>
                  <Input
                    name="postalCode"
                    value={checkoutForm.postalCode}
                    onChange={handleCheckoutChange}
                    required
                    className={isDark ? 'bg-slate-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}
                    placeholder="9000"
                    data-testid="checkout-postal"
                  />
                </div>
              </div>

              <div className={`text-sm p-3 rounded-lg ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                <p>Betaling wordt geïnd bij afhaling in de club of via bankoverschrijving. U ontvangt een orderbevestiging per e-mail.</p>
              </div>

              <DialogFooter className="gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setCheckoutOpen(false)}
                  className={isDark ? 'border-white/20 text-white' : 'border-slate-300 text-slate-700'}
                >
                  Annuleren
                </Button>
                <Button 
                  type="submit" 
                  disabled={processing}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="place-order"
                >
                  {processing ? "Bezig met verwerken..." : "Bestelling plaatsen"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Product Detail Modal with Image Slider */}
        <Dialog open={productDetailOpen} onOpenChange={setProductDetailOpen}>
          <DialogContent className={`max-w-2xl ${isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'}`}>
            {selectedProduct && (
              <>
                <DialogHeader>
                  <DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>{selectedProduct.name}</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  {/* Image Slider */}
                  {(() => {
                    const images = getProductImages(selectedProduct);
                    if (images.length === 0) {
                      return (
                        <div className={`aspect-video flex items-center justify-center rounded-lg ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                          <ShoppingBag className={`h-20 w-20 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
                        </div>
                      );
                    }
                    return (
                      <div className="relative">
                        <div className="aspect-video overflow-hidden rounded-lg">
                          <img 
                            src={images[currentImageIndex]} 
                            alt={`${selectedProduct.name} - ${currentImageIndex + 1}`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        
                        {images.length > 1 && (
                          <>
                            {/* Navigation Arrows */}
                            <Button
                              size="icon"
                              variant="outline"
                              className={`absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full ${isDark ? 'bg-slate-900/80 border-white/20 text-white hover:bg-slate-800' : 'bg-white/80 border-slate-300 text-slate-900 hover:bg-white'}`}
                              onClick={prevImage}
                              data-testid="prev-image"
                            >
                              <ChevronLeft className="h-6 w-6" />
                            </Button>
                            <Button
                              size="icon"
                              variant="outline"
                              className={`absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full ${isDark ? 'bg-slate-900/80 border-white/20 text-white hover:bg-slate-800' : 'bg-white/80 border-slate-300 text-slate-900 hover:bg-white'}`}
                              onClick={nextImage}
                              data-testid="next-image"
                            >
                              <ChevronRight className="h-6 w-6" />
                            </Button>
                            
                            {/* Dots Indicator */}
                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                              {images.map((_, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => setCurrentImageIndex(idx)}
                                  className={`h-2 w-2 rounded-full transition-all ${idx === currentImageIndex 
                                    ? 'bg-blue-600 w-4' 
                                    : isDark ? 'bg-white/50 hover:bg-white/80' : 'bg-slate-400 hover:bg-slate-600'}`}
                                  data-testid={`image-dot-${idx}`}
                                />
                              ))}
                            </div>
                          </>
                        )}
                        
                        {/* Image Counter */}
                        {images.length > 1 && (
                          <div className={`absolute top-3 right-3 px-2 py-1 rounded text-sm ${isDark ? 'bg-slate-900/80 text-white' : 'bg-white/80 text-slate-900'}`}>
                            {currentImageIndex + 1} / {images.length}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Thumbnail Strip */}
                  {(() => {
                    const images = getProductImages(selectedProduct);
                    if (images.length > 1) {
                      return (
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {images.map((img, idx) => (
                            <button
                              key={idx}
                              onClick={() => setCurrentImageIndex(idx)}
                              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                                idx === currentImageIndex 
                                  ? 'border-blue-600' 
                                  : isDark ? 'border-white/10 hover:border-white/30' : 'border-slate-200 hover:border-slate-400'
                              }`}
                              data-testid={`thumbnail-${idx}`}
                            >
                              <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Product Info */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className={`text-2xl font-bold ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-blue-600'}`}>
                        {formatPrice(selectedProduct.price)}
                      </span>
                      {!selectedProduct.in_stock && (
                        <Badge variant="outline" className="text-red-500 border-red-500/50">
                          Uitverkocht
                        </Badge>
                      )}
                    </div>
                    <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>
                      {selectedProduct.description}
                    </p>
                    <Badge variant="outline" className={isDark ? 'border-white/20 text-slate-400' : 'border-slate-300 text-slate-600'}>
                      {selectedProduct.category}
                    </Badge>
                  </div>
                </div>

                <DialogFooter className="gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setProductDetailOpen(false)}
                    className={isDark ? 'border-white/20 text-white' : 'border-slate-300 text-slate-700'}
                  >
                    Sluiten
                  </Button>
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={!selectedProduct.in_stock}
                    onClick={() => {
                      addToCart(selectedProduct);
                      setProductDetailOpen(false);
                    }}
                    data-testid="add-to-cart-modal"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Toevoegen aan Winkelwagen
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Contact for Custom Orders */}
        <section className={`mt-16 p-8 rounded-lg text-center ${isDark ? 'bg-slate-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-md'}`}>
          <h3 className={`text-xl font-bold mb-4 font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Op zoek naar meer?
          </h3>
          <p className={`mb-6 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Bezoek onze club voor meer merchandise-opties of neem contact met ons op voor aangepaste bestellingen.
          </p>
          <Button asChild className="bg-[hsl(217,91%,60%)] hover:bg-[hsl(217,91%,50%)]">
            <a href="/contact">Neem contact op voor bestellingen</a>
          </Button>
        </section>
      </div>
    </div>
  );
}

export default function Shop() {
  return (
    <RecaptchaWrapper>
      <ShopPage />
    </RecaptchaWrapper>
  );
}
